/**
 * Delivery date field on floral products.
 *
 * Sets the selectable range, refuses a day we are closed, and shows a notice
 * when today is chosen. Kept out
 * of assets/product-custom-property.js on purpose: that component's handleInput
 * does character counting and dereferences refs.characterCount, which a date
 * field never renders.
 *
 * Classic script, not a module, matching floral-cardstock-addon.js. Loaded
 * site-wide from layout/theme.liquid, which is the only script path in this
 * theme that has proved reliable (the script_tag filter emits nothing, and raw
 * tags in a section body get stripped).
 */
(function () {
  "use strict";

  /**
   * CLOSED DAYS (2026-10-01). Sundays, holidays and blackout dates used to be
   * choosable here, and the order went through. The browser's date input has
   * no way to grey out single days, so a closed day is refused instead: the
   * field is marked invalid with the sentence below, which stops the add to
   * cart and shows the reason under the field.
   *
   * Sunday is refused from the start. Holidays and blackouts come from the
   * same deliveryWindows endpoint delivery-windows.js reads (one request,
   * shared). If it fails, only Sundays are refused and the order is checked
   * by the team as before. The app server refuses closed days on its own;
   * this is the website's courtesy, not the authority.
   */
  var ENDPOINT = "https://us-central1-twelve-thirty-four.cloudfunctions.net/deliveryWindows";
  var CLOSED_MESSAGE = "We are closed that day. Please choose another.";
  var closedWeekdays = [0];
  var closedDates = {};
  var updaters = [];
  var closedLoaded = false;

  /** Shared with delivery-windows.js. Resolves null on failure, never rejects. */
  function hours() {
    if (!window.__ttfDeliveryHours) {
      window.__ttfDeliveryHours = fetch(ENDPOINT, { method: "GET", mode: "cors" })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; })
        .then(function (data) {
          // A failure is not kept, so the next re-render asks again.
          if (!data) window.__ttfDeliveryHours = null;
          return data;
        });
    }
    return window.__ttfDeliveryHours;
  }

  /** Asked for on the first date field wired, so pages without one never call. */
  function loadClosedDays() {
    if (closedLoaded) return;
    hours().then(function (data) {
      if (!data || closedLoaded) return;
      closedLoaded = true;
      if (Array.isArray(data.closedWeekdays) && data.closedWeekdays.length) {
        var days = data.closedWeekdays.filter(function (d) { return d >= 0 && d <= 6; });
        // Sunday stays closed whatever arrives.
        if (days.indexOf(0) === -1) days.push(0);
        closedWeekdays = days;
      }
      if (Array.isArray(data.closedDates)) {
        data.closedDates.forEach(function (d) {
          if (typeof d === "string") closedDates[d] = true;
        });
      }
      updaters = updaters.filter(function (u) { return u.input.isConnected; });
      updaters.forEach(function (u) { u.update(); });
    });
  }

  /** 0 = Sunday, read from the YYYY-MM-DD the field holds, never the clock. */
  function weekdayOf(ymd) {
    var p = ymd.split("-");
    return new Date(Date.UTC(+p[0], +p[1] - 1, +p[2], 12)).getUTCDay();
  }

  function isClosed(ymd) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd || "")) return false;
    if (closedWeekdays.indexOf(weekdayOf(ymd)) !== -1) return true;
    return closedDates[ymd] === true;
  }

  /**
   * Local-time YYYY-MM-DD. Copied from assets/gift-card-recipient-form.js.
   *
   * Deliberately NOT toISOString().slice(0,10): that is UTC, so any Dallas
   * customer ordering after 7pm CDT would have "today" resolve to tomorrow and
   * the min attribute would forbid the day they are standing in.
   */
  function localYMD(date) {
    var year = date.getFullYear();
    var month = String(date.getMonth() + 1).padStart(2, "0");
    var day = String(date.getDate()).padStart(2, "0");
    return year + "-" + month + "-" + day;
  }

  function wire(input) {
    if (input.dataset.deliveryDateWired === "1") return;
    input.dataset.deliveryDateWired = "1";

    var today = new Date();
    var maxDays = parseInt(input.dataset.maxDaysOut, 10);
    if (!Number.isFinite(maxDays) || maxDays <= 0) maxDays = 60;
    var max = new Date();
    max.setDate(today.getDate() + maxDays);

    var todayYMD = localYMD(today);
    input.setAttribute("min", todayYMD);
    input.setAttribute("max", localYMD(max));

    var wrapper = input.parentElement;
    var notice = wrapper && wrapper.querySelector("[data-delivery-date-notice]");
    var noticeText = notice && notice.querySelector("span");

    function update() {
      var closed = isClosed(input.value);
      input.setCustomValidity(closed ? CLOSED_MESSAGE : "");
      if (closed) input.setAttribute("aria-invalid", "true");
      else input.removeAttribute("aria-invalid");
      if (!notice || !noticeText) return closed;
      if (closed) {
        noticeText.textContent = CLOSED_MESSAGE;
        notice.hidden = false;
      } else if (input.value === todayYMD) {
        noticeText.textContent = input.dataset.sameDayNotice || "";
        notice.hidden = false;
      } else {
        notice.hidden = true;
      }
      return closed;
    }

    input.addEventListener("change", function () {
      // Say it at once, rather than only when they try to add to cart.
      if (update() && typeof input.reportValidity === "function") input.reportValidity();
    });
    input.addEventListener("input", update);
    updaters.push({ input: input, update: update });
    update();
    loadClosedDays();
  }

  /* The reveal toggle lived here until 2026-09-14. It stripped its own name so
     the checkbox never became a line item property, and disabled the date input
     while hidden so an empty required value could not block the form. Both were
     the right calls for an OPTIONAL date. The date is required now, so the
     toggle is gone and the field is simply always there. */

  function scan(root) {
    (root || document).querySelectorAll("[data-delivery-date-input]").forEach(wire);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { scan(); });
  } else {
    scan();
  }

  // Quick-add and variant changes re-render product markup, so re-scan rather
  // than assuming one pass at load is enough.
  document.addEventListener("shopify:section:load", function (e) { scan(e.target); });
  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      for (var j = 0; j < records[i].addedNodes.length; j++) {
        var n = records[i].addedNodes[j];
        if (n.nodeType === 1) scan(n);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
